import React from "react";
import { Link } from "react-router-dom";
import { Check, Building2, Scale } from "lucide-react";
import SEO from "@/components/SEO";
import { PageHero, CTABand } from "@/components/PageHero";
import { Section, SectionHeading, WarningBox, InfoBox } from "@/components/Primitives";
import { MIGRATION_WARNING } from "@/data/content";

const IMG = "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const CLIENTS = ["Malaysian entrepreneurs", "Pakistani business owners", "Indian business owners", "International founders", "Exporters", "Active investors", "Families considering an international move"];
const SERVICES = ["Business purchase support", "New business establishment", "Existing company expansion", "Investment opportunity review", "Market-entry research", "Business-plan coordination", "Exploratory visit planning", "Licensed immigration professional referral"];
const GPC_HANDLES = ["Commercial assessment", "Market-entry planning", "Business setup coordination", "Acquisition support", "Preliminary opportunity review", "Documentation coordination"];
const IMMIG_HANDLES = ["Immigration legal advice", "Program eligibility", "Immigration strategy", "Representation", "Filing and communication with immigration authorities"];
const DESTINATIONS = ["Canada", "United Kingdom", "Malaysia", "Other markets (static configuration placeholders)"];

const Tick = ({ children }) => (<li className="flex items-start gap-3 text-sm text-navy"><span className="w-5 h-5 rounded-full bg-emerald-brand/10 flex items-center justify-center shrink-0 mt-0.5"><Check size={12} className="text-emerald-brand" /></span>{children}</li>);

export default function InvestmentMigration() {
  return (
    <>
      <SEO title="Business & Investment Migration" description="Build a real business and explore a real pathway. Commercial assessment and coordination with licensed immigration professionals. Contact us to know more." path="/business-investment-migration" />
      <PageHero eyebrow="Business & Investment Migration" image={IMG}
        title="Build a Real Business. Explore a Real Pathway."
        subtitle="For some entrepreneurs, international expansion connects with the goal of living and working in another country. We assess the business side and coordinate immigration matters with appropriately licensed professionals." />

      <Section className="bg-white">
        <div className="mb-10"><WarningBox testid="regulatory-warning-box">{MIGRATION_WARNING}</WarningBox></div>

        <div className="grid lg:grid-cols-2 gap-8">
          <div>
            <h2 className="text-xl font-bold text-navy mb-4">Who We Work With</h2>
            <ul className="grid sm:grid-cols-2 gap-3">{CLIENTS.map((c) => <Tick key={c}>{c}</Tick>)}</ul>
            <h2 className="text-xl font-bold text-navy mt-8 mb-4">Destinations</h2>
            <ul className="grid sm:grid-cols-2 gap-3">{DESTINATIONS.map((d) => <Tick key={d}>{d}</Tick>)}</ul>
          </div>
          <div>
            <h2 className="text-xl font-bold text-navy mb-4">Our Services</h2>
            <ul className="grid sm:grid-cols-2 gap-3">{SERVICES.map((s) => <Tick key={s}>{s}</Tick>)}</ul>
          </div>
        </div>
      </Section>

      <Section className="bg-slate-50">
        <SectionHeading eyebrow="Clear Responsibilities" title="Who Does What" center />
        <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
          <div className="rounded-2xl bg-white border border-slate-100 p-7">
            <div className="flex items-center gap-2 mb-4"><Building2 className="text-emerald-brand" /><h3 className="font-bold text-navy">Global Pass Consultants handles</h3></div>
            <ul className="space-y-2.5">{GPC_HANDLES.map((g) => <Tick key={g}>{g}</Tick>)}</ul>
          </div>
          <div className="rounded-2xl bg-navy text-white p-7">
            <div className="flex items-center gap-2 mb-4"><Scale className="text-gold" /><h3 className="font-bold">Licensed immigration professionals handle</h3></div>
            <ul className="space-y-2.5">{IMMIG_HANDLES.map((g) => (
              <li key={g} className="flex items-start gap-3 text-sm text-slate-200"><span className="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center shrink-0 mt-0.5"><Check size={12} className="text-gold" /></span>{g}</li>
            ))}</ul>
          </div>
        </div>
        <div className="mt-8 max-w-5xl mx-auto"><InfoBox>We do not publish details of any specific immigration program unless the information is current and reviewed by an authorized immigration professional in the relevant country. Contact us to know more.</InfoBox></div>
        <div className="mt-6 text-center"><Link to="/eligibility" className="btn-gold rounded-full px-7 py-3.5 font-bold">Request a Business Mobility Assessment</Link></div>
      </Section>

      <CTABand service="Business and investment migration" serviceKey="migration" />
    </>
  );
}
